import React from 'react';
import { resolveDeviceDisplayName } from '../../../utils/deviceDisplayName';

/**
 * Get condition label based on grade value
 */
export const getConditionLabel = (value: string, language: string): string => {
  const conditionLabels: Record<string, Record<string, string>> = {
    A: {
      nl: 'Zo goed als nieuw', en: 'Like new', fr: 'Comme neuf', de: 'Wie neu', es: 'Como nuevo', tr: 'Yeni gibi', bg: 'Като нов', ro: 'Ca nou', sk: 'Ako nový'
    },
    B: {
      nl: 'Goede staat', en: 'Good condition', fr: 'Bon état', de: 'Guter Zustand', es: 'Buen estado', tr: 'İyi durumda', bg: 'Добро състояние', ro: 'În stare bună', sk: 'Dobrý stav'
    },
    C: {
      nl: 'Zichtbare gebruikssporen', en: 'Visible wear', fr: 'Usure visible', de: 'Sichtbare Abnutzung', es: 'Desgaste visible', tr: 'Gözle görülür aşınma', bg: 'Видимо износване', ro: 'Uzură vizibilă', sk: 'Viditeľné opotrebenie'
    },
    D: {
      nl: 'Duidelijke beschadigingen', en: 'Clear damage', fr: 'Dommages visibles', de: 'Deutliche Beschädigungen', es: 'Daños evidentes', tr: 'Belirgin hasarlar', bg: 'Явни повреди', ro: 'Deteriorări evidente', sk: 'Zjavné poškodenia'
    }
  };

  return conditionLabels[value]?.[language] || value;
};

/**
 * Get battery label for display.
 * With `inSentence` the first letter is lowercased so the label reads naturally
 * inside a sentence (e.g. "... van onder 85%").
 */
export const getBatteryLabel = (value: string, language: string = 'nl', inSentence: boolean = false): string => {
  const labels: Record<string, Record<string, string>> = {
    above95: { nl: 'Boven 95%', en: 'Above 95%', fr: 'Plus de 95 %', de: 'Über 95 %', es: 'Más del 95 %', tr: '%95 üzeri', bg: 'Над 95%', ro: 'Peste 95%', sk: 'Nad 95%' },
    above90: { nl: '90% - 95%', en: '90% - 95%', fr: '90 % - 95 %', de: '90 % - 95 %', es: '90 % - 95 %', tr: '%90 - %95', bg: '90% - 95%', ro: '90% - 95%', sk: '90% - 95%' },
    above85: { nl: '85% - 90%', en: '85% - 90%', fr: '85 % - 90 %', de: '85 % - 90 %', es: '85 % - 90 %', tr: '%85 - %90', bg: '85% - 90%', ro: '85% - 90%', sk: '85% - 90%' },
    below85: { nl: 'Onder 85%', en: 'Under 85%', fr: 'Moins de 85 %', de: 'Unter 85 %', es: 'Menos de 85 %', tr: '%85 altı', bg: 'Под 85%', ro: 'Sub 85%', sk: 'Pod 85%' },
    unknown: { nl: 'Onbekend', en: 'Unknown', fr: 'Inconnu', de: 'Unbekannt', es: 'Desconocido', tr: 'Bilinmiyor', bg: 'Неизвестен', ro: 'Necunoscut', sk: 'Neznámy' }
  };

  const label = labels[value]?.[language] || labels[value]?.nl || value;
  if (!inSentence) return label;
  return label.charAt(0).toLowerCase() + label.slice(1);
};

/**
 * Number/currency locale for a UI language (used for price formatting)
 */
const NUMBER_LOCALES: Record<string, string> = {
  nl: 'nl-NL',
  en: 'en-GB',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  tr: 'tr-TR',
  bg: 'bg-BG',
  ro: 'ro-RO',
  sk: 'sk-SK'
};

export const getLocaleForLanguage = (language: string): string => {
  return NUMBER_LOCALES[language] || 'nl-NL';
};

/**
 * Get translated value for spec display
 */
export const getTranslatedValue = (key: string, value: string | undefined, language: string = 'nl'): React.ReactNode => {
  if (!value) return '';

  // General condition mapping coming from letter grades
  if (key === 'condition') {
    return getConditionLabel(value, language);
  }

  // Battery percentage helper used in multiple keys
  const unknownLabels: Record<string, string> = {
    nl: 'Onbekend',
    en: 'Unknown',
    fr: 'Inconnu',
    de: 'Unbekannt',
    es: 'Desconocido',
    tr: 'Bilinmiyor',
    bg: 'Неизвестен',
    ro: 'Necunoscut',
    sk: 'Neznámy'
  };

  // Legacy battery key shows visual indicator
  if (key === 'battery') {
    if (value === 'unknown') {
      return unknownLabels[language] || 'Unknown';
    }
    const map: Record<string, Record<string, string>> = {
      above95: { nl: 'Boven 95%', en: 'Above 95%', fr: 'Plus de 95 %', de: 'Über 95 %', es: 'Más del 95 %', tr: '%95 üzeri', bg: 'Над 95%', ro: 'Peste 95%', sk: 'Nad 95%' },
      above90: { nl: '90% - 100%', en: '90% - 100%', fr: '90 % - 100 %', de: '90 % - 100 %', es: '90 % - 100 %', tr: '%90 - %100', bg: '90% - 100%', ro: '90% - 100%', sk: '90% - 100%' },
      above85: { nl: '85% - 100%', en: '85% - 100%', fr: '85 % - 100 %', de: '85 % - 100 %', es: '85 % - 100 %', tr: '%85 - %100', bg: '85% - 100%', ro: '85% - 100%', sk: '85% - 100%' },
      below85: { nl: 'Onder 85%', en: 'Under 85%', fr: 'Moins de 85 %', de: 'Unter 85 %', es: 'Menos de 85 %', tr: '85% altı', bg: 'Под 85%', ro: 'Sub 85%', sk: 'Pod 85%' }
    };
    return map[value]?.[language] || value;
  }

  // iPhone specific fields
  if (key === 'functionality') {
    const map: Record<string, Record<string, string>> = {
      works: {
        nl: 'Werkt perfect', en: 'Works perfectly', fr: 'Fonctionne parfaitement', de: 'Funktioniert einwandfrei', es: 'Funciona perfectamente', tr: 'Mükemmel çalışıyor', bg: 'Работи перфектно', ro: 'Funcționează perfect', sk: 'Funguje perfektne'
      },
      issues: {
        nl: 'Heeft problemen', en: 'Has issues', fr: 'A des problèmes', de: 'Hat Probleme', es: 'Tiene problemas', tr: 'Sorunları var', bg: 'Има проблеми', ro: 'Are probleme', sk: 'Má problémy'
      }
    };
    return map[value]?.[language] || value;
  }

  if (key === 'screenCondition' || key === 'housingCondition') {
    const map: Record<string, Record<string, string>> = {
      perfect: {
        nl: 'Perfect', en: 'Perfect', fr: 'Parfait', de: 'Perfekt', es: 'Perfecto', tr: 'Mükemmel', bg: 'Перфектно', ro: 'Perfect', sk: 'Perfektný'
      },
      minor: {
        nl: 'Kleine schade', en: 'Minor damage', fr: 'Dommages mineurs', de: 'Leichte Schäden', es: 'Daños menores', tr: 'Küçük hasar', bg: 'Леки повреди', ro: 'Deteriorări minore', sk: 'Menšie poškodenie'
      },
      major: {
        nl: 'Grote schade', en: 'Major damage', fr: 'Dommages importants', de: 'Starke Schäden', es: 'Daños mayores', tr: 'Büyük hasar', bg: 'Сериозни повреди', ro: 'Deteriorări majore', sk: 'Vážne poškodenie'
      }
    };
    return map[value]?.[language] || value;
  }

  if (key === 'batteryCapacity') {
    const map: Record<string, Record<string, string>> = {
      above85: {
        nl: '85% - 100%', en: '85% - 100%', fr: '85 % - 100 %', de: '85 % - 100 %', es: '85 % - 100 %', tr: '%85 - %100', bg: '85% - 100%', ro: '85% - 100%', sk: '85% - 100%'
      },
      full: {
        nl: '100% capaciteit', en: '100% capacity', fr: 'Capacité 100 %', de: '100 % Kapazität', es: '100 % de capacidad', tr: '%100 kapasite', bg: '100% капацитет', ro: 'Capacitate 100%', sk: '100% kapacita'
      },
      below85: {
        nl: 'Onder 85%', en: 'Under 85%', fr: 'Moins de 85 %', de: 'Unter 85 %', es: 'Menos de 85 %', tr: '85% altı', bg: 'Под 85%', ro: 'Sub 85%', sk: 'Pod 85%'
      },
      unknown: unknownLabels
    };
    return map[value]?.[language] || value;
  }

  if (key === 'networkType') {
    const map: Record<string, Record<string, string>> = {
      wifi: { nl: 'Wi‑Fi', en: 'Wi‑Fi', fr: 'Wi‑Fi', de: 'WLAN', es: 'Wi‑Fi', tr: 'Wi‑Fi', bg: 'Wi‑Fi', ro: 'Wi‑Fi', sk: 'Wi‑Fi' },
      cellular: { nl: 'Wi‑Fi + Cellular', en: 'Wi‑Fi + Cellular', fr: 'Wi‑Fi + Cellulaire', de: 'WLAN + Mobilfunk', es: 'Wi‑Fi + Celular', tr: 'Wi‑Fi + Hücresel', bg: 'Wi‑Fi + Мобилни данни', ro: 'Wi‑Fi + Cellular', sk: 'Wi‑Fi + Mobilné' }
    };
    return map[value]?.[language] || value;
  }

  if (key === 'cellularSupport') {
    const map: Record<string, Record<string, string>> = {
      yes: { nl: 'Ja', en: 'Yes', fr: 'Oui', de: 'Ja', es: 'Sí', tr: 'Evet', bg: 'Да', ro: 'Da', sk: 'Áno' },
      no: { nl: 'Nee', en: 'No', fr: 'Non', de: 'Nein', es: 'No', tr: 'Hayır', bg: 'Не', ro: 'Nu', sk: 'Nie' }
    };
    return map[value]?.[language] || value;
  }

  if (key === 'watchSize') {
    const map: Record<string, Record<string, string>> = {
      '38mm': { nl: '38 mm', en: '38 mm', fr: '38 mm', de: '38 mm', es: '38 mm', tr: '38 mm', bg: '38 мм', ro: '38 mm', sk: '38 mm' },
      '40mm': { nl: '40 mm', en: '40 mm', fr: '40 mm', de: '40 mm', es: '40 mm', tr: '40 mm', bg: '40 мм', ro: '40 mm', sk: '40 mm' },
      '41mm': { nl: '41 mm', en: '41 mm', fr: '41 mm', de: '41 mm', es: '41 mm', tr: '41 mm', bg: '41 мм', ro: '41 mm', sk: '41 mm' },
      '44mm': { nl: '44 mm', en: '44 mm', fr: '44 mm', de: '44 mm', es: '44 mm', tr: '44 mm', bg: '44 мм', ro: '44 mm', sk: '44 mm' },
      '45mm': { nl: '45 mm', en: '45 mm', fr: '45 mm', de: '45 mm', es: '45 mm', tr: '45 mm', bg: '45 мм', ro: '45 mm', sk: '45 mm' },
      '49mm': { nl: '49 mm', en: '49 mm', fr: '49 mm', de: '49 mm', es: '49 mm', tr: '49 mm', bg: '49 мм', ro: '49 mm', sk: '49 mm' }
    };
    return map[value]?.[language] || value;
  }

  if (key === 'watchBand') {
    const map: Record<string, Record<string, string>> = {
      sport: { nl: 'Sportband', en: 'Sport Band', fr: 'Bracelet Sport', de: 'Sportarmband', es: 'Correa Sport', tr: 'Spor Kordon', bg: 'Спортна каишка', ro: 'Curea Sport', sk: 'Športový remienok' },
      loop: { nl: 'Lus-band', en: 'Loop Band', fr: 'Bracelet Loop', de: 'Loop-Armband', es: 'Correa Loop', tr: 'Loop Kordon', bg: 'Loop каишка', ro: 'Curea Loop', sk: 'Loop remienok' }
    };
    return map[value]?.[language] || value;
  }

  // For other simple keys (e.g., storage) return raw value
  return value;
};

/**
 * Get spec label for display
 */
export const getSpecLabel = (key: string, language: string): string => {
  const labels: Record<string, Record<string, string>> = {
    storage: { nl: 'Opslag', en: 'Storage', fr: 'Stockage', de: 'Speicher', es: 'Almacenamiento', tr: 'Depolama', bg: 'Съхранение', ro: 'Stocare', sk: 'Uloženie' },
    battery: { nl: 'Batterij', en: 'Battery', fr: 'Batterie', de: 'Akku', es: 'Batería', tr: 'Pil', bg: 'Батерия', ro: 'Baterie', sk: 'Batéria' },
    condition: { nl: 'Conditie', en: 'Condition', fr: 'État', de: 'Zustand', es: 'Condición', tr: 'Durum', bg: 'Състояние', ro: 'Stare', sk: 'Stav' },
    networkType: { nl: 'Netwerktype', en: 'Network type', fr: 'Type de réseau', de: 'Netzwerktyp', es: 'Tipo de red', tr: 'Ağ tipi', bg: 'Тип мрежа', ro: 'Tip rețea', sk: 'Typ siete' },
    screenSize: { nl: 'Schermgrootte', en: 'Screen size', fr: "Taille d'écran", de: 'Bildschirmgröße', es: 'Tamaño de pantalla', tr: 'Ekran boyutu', bg: 'Размер на екрана', ro: 'Mărime ecran', sk: 'Veľkosť displeja' },
    modelYear: { nl: 'Modeljaar', en: 'Model year', fr: 'Année du modèle', de: 'Modelljahr', es: 'Año del modelo', tr: 'Model yılı', bg: 'Моделна година', ro: 'An model', sk: 'Modelový rok' },
    processor: { nl: 'Processor', en: 'Processor', fr: 'Processeur', de: 'Prozessor', es: 'Procesador', tr: 'İşlemci', bg: 'Процесор', ro: 'Procesor', sk: 'Procesor' },
    ram: { nl: 'RAM-geheugen', en: 'RAM', fr: 'Mémoire RAM', de: 'RAM', es: 'RAM', tr: 'RAM', bg: 'RAM', ro: 'RAM', sk: 'RAM' },
    accessories: { nl: 'Accessoires', en: 'Accessories', fr: 'Accessoires', de: 'Zubehör', es: 'Accesorios', tr: 'Aksesuarlar', bg: 'Аксесоари', ro: 'Accesorii', sk: 'Príslušenstvo' },
    // iPhone specific
    functionality: { nl: 'Functionaliteit', en: 'Functionality', fr: 'Fonctionnalité', de: 'Funktionalität', es: 'Funcionalidad', tr: 'Fonksiyonellik', bg: 'Функционалност', ro: 'Funcționalitate', sk: 'Funkcionalita' },
    screenCondition: { nl: 'Schermconditie', en: 'Screen condition', fr: "État de l'écran", de: 'Bildschirmzustand', es: 'Estado de pantalla', tr: 'Ekran durumu', bg: 'Състояние на екрана', ro: 'Stare ecran', sk: 'Stav displeja' },
    housingCondition: { nl: 'Behuizingconditie', en: 'Housing condition', fr: 'État du boîtier', de: 'Gehäusezustand', es: 'Estado de carcasa', tr: 'Kasa durumu', bg: 'Състояние на корпуса', ro: 'Stare carcasă', sk: 'Stav puzdra' },
    batteryCapacity: { nl: 'Batterijcapaciteit', en: 'Battery capacity', fr: 'Capacité de batterie', de: 'Akkukapazität', es: 'Capacidad de batería', tr: 'Pil kapasitesi', bg: 'Капацитет на батерия', ro: 'Capacitate baterie', sk: 'Kapacita batérie' },
    // Apple Watch specific
    watchSize: { nl: 'Kastgrootte', en: 'Case size', fr: 'Taille du boîtier', de: 'Gehäusegröße', es: 'Tamaño de caja', tr: 'Kasa boyutu', bg: 'Размер на корпуса', ro: 'Dimensiune carcasă', sk: 'Veľkosť puzdra' },
    watchBand: { nl: 'Horlogeband', en: 'Watch band', fr: 'Bracelet', de: 'Armband', es: 'Correa', tr: 'Saat kayışı', bg: 'Каишка', ro: 'Curea', sk: 'Remienok' },
    cellularSupport: { nl: 'Cellular', en: 'Cellular', fr: 'Cellulaire', de: 'Mobilfunk', es: 'Celular', tr: 'Hücresel', bg: 'Мобилни данни', ro: 'Cellular', sk: 'Mobilné' }
  };

  return labels[key]?.[language] || labels[key]?.nl || key;
};

/**
 * Format price for display
 */
export const formatPrice = (
  price: number,
  t?: ((key: string) => string) | Record<string, string>,
  locale: string = 'nl-NL'
): string => {
  if (price <= -1) {
    // Support passing either a translator function t('key') or a translations object
    if (typeof t === 'function') {
      const translated = t('onRequest') || t('opAanvraag');
      return translated || 'Op aanvraag';
    }
    if (t && typeof t === 'object') {
      return t.onRequest || t.opAanvraag || 'Op aanvraag';
    }
    return 'Op aanvraag';
  }
  if (price === 0) {
    // On Demand items arrive with price 0 — show localized "Price on request"
    if (typeof t === 'function') {
      return t('priceOnRequest') || t('onRequest') || 'Prijs op aanvraag';
    }
    if (t && typeof t === 'object') {
      return t.priceOnRequest || t.onRequest || 'Prijs op aanvraag';
    }
    return 'Prijs op aanvraag';
  }
  // Ensure no double Euro symbols or incorrect formatting for other cases
  const formattedPrice = price.toLocaleString(locale);
  // ",-" is a Dutch price convention; other languages show the plain amount
  return locale.startsWith('nl') ? `€ ${formattedPrice},-` : `€ ${formattedPrice}`;
};

/**
 * Customer-facing product name for a cart item.
 *
 * Prefers the display name from the database (stored on the item when the
 * model was selected) and falls back to a readable version of the model key
 * for items added before `displayName` existed. With `isMobile` the release
 * year is dropped to keep the line short.
 */
export const formatProductName = (
  product: string | { modelName: string; displayName?: string | null },
  isMobile: boolean = false
): string => {
  const item = typeof product === 'string' ? { modelName: product } : product;
  return resolveDeviceDisplayName(item.displayName, item.modelName, { short: isMobile });
};
